import React, {
  CSSProperties,
  useEffect,
  useState,
} from 'react'
import ReactToPrint from 'react-to-print'
import { getGroupMamorioQrCodes } from '../../../modules/Rental'
import { errorIcon } from '../../../icons'
import { isSafari, toId } from '../../../utils/commonUtils'
import './PrintBrochureModal.css'
import { useAppDispatch } from '../../../hooks'
import { generateRentalDigitCSV } from '../../../helpers/CommonMamorioHelper'

type PropType = {
  group: IGroup
  printables?: IPrintableQr[]
}

const PrintButton = () => (
  <button type="button" className="btn btn-primary btn-sm">
    印刷する
  </button>
)

const toPrintable = (qrCode: IQrCode): IPrintableQr => ({
  id: Number(qrCode.id),
  name: qrCode.resource_name,
  image_url: qrCode.image_url,
  digit_code: qrCode.digit_code,
})

const PrintBrochureModal = ({
  group,
  printables,
}: PropType) => {
  const dispatch = useAppDispatch()

  const [printableItems, setPrintableItems] = useState<IPrintableQr[]>(printables ?? [])
  const [downloadableCodes, setDownloadableCodes] = useState<IQrCode[]>([])

  let printContentRef: React.ReactInstance | null = null

  const createColumn = (printable: IPrintableQr, width: { width: string }) => (
    <td className="print-col" key={`${Date.now()}${printable.id}`} style={width}>
      <div className="print-col-inside">
        <div className="print-col-image">
          <img src={printable.image_url ?? errorIcon} className="print-col-qr" alt="qr-code" />
        </div>
        <div className="print-col-desc">
          <div className="print-col-title">
            {printable.name}
          </div>
          <div className="print-col-code">
            {printable.digit_code}
          </div>
        </div>
      </div>
    </td>
  )

  const emptyColumn = (index: number, width: CSSProperties) => (
    <td className="print-col" key={index} style={width} />
  )

  const createPrintContent = (printableList: IPrintableQr[]) => {
    const columnsPerRow = 2
    const pageBreakAt = isSafari ? 7 : 9
    const width = { width: `${100 / columnsPerRow}%` }
    const rows = []

    let index = 0

    for (let i = 1; i <= Math.ceil(printableList.length / columnsPerRow); i += 1) {
      const cols = []
      let leftToPrint = columnsPerRow
      for (let c = index; c < Math.min(columnsPerRow * i, printableList.length); c += 1) {
        const printable = printableList[c]

        if (printable) {
          cols.push(createColumn(printable, width))
          index += 1
          leftToPrint -= 1
        }
      }

      // If number of Mamorio is odd
      if (leftToPrint !== 0) {
        for (let l = 0; l < leftToPrint; l += 1) {
          cols.push(emptyColumn(index + l, width))
        }
      }
      rows.push(
        <div className="print-row" key={index}>
          <table>
            <tbody>
            <tr>
              {cols}
            </tr>
            </tbody>
          </table>
        </div>,
      )

      if (i % pageBreakAt === 0 && index < printableList.length) {
        rows.push(
          <footer key={`footer${index}`} />,
        )
      }
    }
    return rows
  }

  useEffect(() => {
    if (printables) {
      setPrintableItems(printables)
      setDownloadableCodes([])
      return
    }

    if (!group?.id) return

    dispatch(getGroupMamorioQrCodes({
      groupId: toId<GroupId>(group.id),
    }))
      .unwrap()
      .then((qrCodes) => {
        setPrintableItems(qrCodes.map(toPrintable))
        setDownloadableCodes(qrCodes)
      })
      .catch((e) => {
        throw new Error(e.toString())
      })
  }, [dispatch, group, printables])

  return (
    <div className="print-brochure">
      <div className="button-section">
        <ReactToPrint
          trigger={PrintButton}
          content={() => printContentRef}
          bodyClass="print-brochure"
        />
        {printables == null && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => generateRentalDigitCSV(downloadableCodes)}
          >
            CSVをダウンロード
          </button>
        )}
      </div>
      <div className="print-brochure-content" ref={(el) => { printContentRef = el }}>
        {
          createPrintContent(printableItems)
        }
      </div>
    </div>
  )
}

export default PrintBrochureModal
